"use client";

import { useMemo, useState } from "react";
import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  bindCoverLetterText,
  type CoverLetterForm,
  type CoverLetterUpdate,
  DeclarationFields,
  EMPTY_COVER_LETTER,
  ManuscriptFields,
  SignatureFields,
} from "@/components/tools/academic-correspondence/cover-letter-fields";
import {
  CompactTextArea,
  DraftPanel,
  FieldGroup,
  FormHeader,
} from "@/components/tools/academic-correspondence/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { countPlaceholders } from "@/lib/tool-kit/correspondence/compose";
import {
  buildCoverLetter,
  COVER_LETTER_BANK,
  EDITOR_TITLES,
  type EditorTitle,
  formatLetterDate,
  PHRASINGS,
  type Phrasing,
} from "@/lib/tool-kit/correspondence/journal-cover-letter";

/** English strings. A translated content file overrides these through `ui`. */
export const JOURNAL_COVER_LETTER_GENERATOR_STRINGS = {
  formHeading: "Your submission",
  tryExample: "Try an example",
  clear: "Clear",
  journalSection: "Journal and editor",
  editorTitleLabel: "Editor's title",
  editorTitleOptions: {
    dr: "Dr.",
    drNoStop: "Dr (no full stop, British style)",
    professor: "Professor",
    none: "No title",
  },
  editorNameLabel: "Editor's family name (optional)",
  editorNamePlaceholder: "Lindqvist",
  editorNameHint:
    "The handling editor or editor-in-chief, from the journal's editorial board page. Leave blank for “Dear Editor”.",
  journalLabel: "Journal",
  journalPlaceholder: "Wetland Ecology Letters",
  dateLabel: "Date (optional)",
  manuscriptSection: "Manuscript",
  titleLabel: "Manuscript title",
  titlePlaceholder:
    "Restored urban wetlands recover bird diversity within five years",
  articleTypeLabel: "Article type",
  articleTypeOptions: {
    research: "Original research article",
    review: "Review article",
    systematic: "Systematic review",
    short: "Short communication",
    methods: "Methods article",
    caseReport: "Case report",
    perspective: "Perspective",
    other: "Other (type the journal's term)",
  },
  otherTypeLabel: "Article type, as the journal names it",
  otherTypePlaceholder: "data paper",
  soleAuthorLabel: "I am the only author",
  pitchSection: "Why the editor should read it",
  findingsLabel: "Main findings (two or three sentences)",
  findingsPlaceholder:
    "We surveyed birds at 24 restored and 12 natural wetlands…",
  significanceLabel: "Why it matters",
  significancePlaceholder:
    "Cities are investing in wetland restoration, yet little is known about…",
  fitLabel: "Why it suits this journal (finish the sentence “…because”)",
  fitPlaceholder:
    "the journal has recently published several studies on restoration outcomes",
  declarationsSection: "Declarations",
  originalLabel:
    "The manuscript is original and has not been published elsewhere",
  notUnderLabel: "It is not under consideration by another journal",
  approvedLabel: "All authors have approved the submission",
  competingLabel: "Competing interests",
  competingOptions: {
    none: "None to declare",
    declare: "Declare them",
  },
  competingTextLabel: "Competing interests to declare",
  competingTextPlaceholder: "M.A. has received speaker fees from …",
  preprintLabel: "Preprint (optional)",
  preprintPlaceholder: "the server and the preprint's DOI link",
  otherLabel: "Anything else the editor should know (optional)",
  otherPlaceholder: "An earlier version was presented at …",
  reviewersSection: "Reviewers (optional)",
  reviewersHint:
    "Many journals ask for both. Check the journal's conflict-of-interest rules before you suggest anyone.",
  suggestedLabel: "Suggested reviewers, one per line",
  suggestedPlaceholder: "Name, institution, email — area of expertise",
  opposedLabel: "Reviewers to exclude, one per line, with a brief reason",
  opposedPlaceholder: "Name, institution — reason",
  signatureSection: "Signature",
  nameLabel: "Corresponding author",
  namePlaceholder: "Mateo Alvarez",
  affiliationLabel: "Affiliation",
  affiliationPlaceholder: "Department of Ecology, University of Lisbon",
  emailLabel: "Email",
  emailPlaceholder: "m.alvarez@example.org",
  signOffLabel: "Sign-off",
  phrasingLabel: "Phrasing",
  phrasingOptions: {
    v1: "Version 1",
    v2: "Version 2",
    v3: "Version 3",
  },
  phrasingHint:
    "Each version words the opening, the fit sentence, and the close differently.",
  draftHeading: "Your cover letter",
  copy: "Copy",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  download: "Download .txt",
  downloadFilename: "cover-letter.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders:
    "No placeholders left. Check the journal's author guidelines for anything else the letter must state.",
  example: {
    editorTitle: "dr",
    editorName: "Lindqvist",
    journal: "Wetland Ecology Letters",
    title: "Restored urban wetlands recover bird diversity within five years",
    findings:
      "We surveyed breeding birds at 24 restored and 12 natural wetlands in three cities over four seasons. Restored sites matched the species richness of natural sites within five years, but marsh specialists returned more slowly than generalist species.",
    significance:
      "Cities are investing heavily in wetland restoration, yet there is little evidence on how quickly restored sites support wildlife. Our results give planners a realistic timeline and identify the species that need targeted help.",
    fit: "the journal has recently published several studies of restoration outcomes, and our multi-city design speaks directly to its interest in applied urban ecology",
    suggested:
      "Dr. Amina Yusuf, University of Nairobi, a.yusuf@example.org — urban bird ecology\nProfessor Lars Holm, Lund University, lars.holm@example.org — wetland restoration",
    name: "Mateo Alvarez",
    affiliation: "Department of Ecology, University of Lisbon",
    email: "m.alvarez@example.org",
  },
  ...COVER_LETTER_BANK,
} as const;

export default function JournalCoverLetterGeneratorWidget() {
  const strings = useToolUi(JOURNAL_COVER_LETTER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [form, setForm] = useState<CoverLetterForm>(EMPTY_COVER_LETTER);

  const update: CoverLetterUpdate = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const text = bindCoverLetterText(form, update);
  const paragraphs = useMemo(
    () =>
      buildCoverLetter(
        { ...form, date: formatLetterDate(form.dateIso, locale) },
        strings
      ),
    [form, locale, strings]
  );
  const blanks = countPlaceholders(paragraphs);
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;

  function fillExample() {
    const { editorTitle, ...rest } = strings.example;
    setForm({
      ...EMPTY_COVER_LETTER,
      ...rest,
      editorTitle: editorTitle as EditorTitle,
    });
  }

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={() => setForm(EMPTY_COVER_LETTER)}
          onExample={fillExample}
        />

        <FieldGroup title={strings.journalSection}>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="jcl-editor-title"
              label={strings.editorTitleLabel}
              onChange={(value: EditorTitle) => update("editorTitle", value)}
              options={EDITOR_TITLES.map((key) => ({
                value: key,
                label: strings.editorTitleOptions[key],
              }))}
              value={form.editorTitle}
            />
            <TextField
              {...text("editorName")}
              hint={strings.editorNameHint}
              label={strings.editorNameLabel}
              placeholder={strings.editorNamePlaceholder}
            />
            <TextField
              {...text("journal")}
              label={strings.journalLabel}
              placeholder={strings.journalPlaceholder}
            />
            <TextField
              id="jcl-date"
              label={strings.dateLabel}
              onChange={(value) => update("dateIso", value)}
              type="date"
              value={form.dateIso}
            />
          </div>
        </FieldGroup>

        <ManuscriptFields form={form} strings={strings} update={update} />

        <FieldGroup title={strings.pitchSection}>
          <CompactTextArea
            {...text("findings")}
            label={strings.findingsLabel}
            placeholder={strings.findingsPlaceholder}
            rows={4}
          />
          <CompactTextArea
            {...text("significance")}
            label={strings.significanceLabel}
            placeholder={strings.significancePlaceholder}
            rows={3}
          />
          <CompactTextArea
            {...text("fit")}
            label={strings.fitLabel}
            placeholder={strings.fitPlaceholder}
            rows={2}
          />
        </FieldGroup>

        <DeclarationFields form={form} strings={strings} update={update} />

        <FieldGroup title={strings.reviewersSection}>
          <p className={HINT}>{strings.reviewersHint}</p>
          <CompactTextArea
            {...text("suggested")}
            label={strings.suggestedLabel}
            placeholder={strings.suggestedPlaceholder}
            rows={3}
          />
          <CompactTextArea
            {...text("opposed")}
            label={strings.opposedLabel}
            placeholder={strings.opposedPlaceholder}
            rows={2}
          />
        </FieldGroup>

        <SignatureFields form={form} strings={strings} update={update} />

        <div className="space-y-2">
          <PillGroup
            label={strings.phrasingLabel}
            name="jcl-phrasing"
            onChange={(value: Phrasing) => update("phrasing", value)}
            options={PHRASINGS.map((key) => ({
              value: key,
              label: strings.phrasingOptions[key],
            }))}
            value={form.phrasing}
          />
          <p className={HINT}>{strings.phrasingHint}</p>
        </div>
      </section>

      <DraftPanel
        filename={strings.downloadFilename}
        heading={strings.draftHeading}
        paragraphs={paragraphs}
        status={status}
        strings={strings}
      />
    </>
  );
}
